import { useState } from 'react';

import { type RejectPreview, useRejectIndustry, useRejectPreview } from '@/api/queries';
import type { Industry } from '@/api/types';
import { Modal } from '@/ui/Modal';
import { FormError } from '@/ui/StateCard';
import { ActionButton, buttonState } from '@/ui/ActionButton';

function plural(count: number, one: string, many: string): string {
  return `${count} ${count === 1 ? one : many}`;
}

/** "2 opportunities, 1 asset and 1 case": what uses the industry now, leaving out what has none. */
export function usage(counts: RejectPreview): string {
  const parts = [
    counts.opportunities ? plural(counts.opportunities, 'opportunity', 'opportunities') : '',
    counts.assets ? plural(counts.assets, 'asset', 'assets') : '',
    counts.cases ? plural(counts.cases, 'success case', 'success cases') : '',
  ].filter(Boolean);
  return parts.length > 1 ? `${parts.slice(0, -1).join(', ')} and ${parts.at(-1)}` : (parts[0] ?? '');
}

function ReplacementField({
  industry,
  counts,
  choices,
  value,
  onChange,
}: {
  industry: Industry;
  counts: RejectPreview;
  choices: Industry[];
  value: string;
  onChange: (id: string) => void;
}) {
  const required = counts.opportunities > 0;
  const chosen = choices.find((i) => i.id === value);
  let hint = `Without a replacement, the ${usage({ ...counts, opportunities: 0 })} only lose this tag.`;
  if (chosen) hint = `They move to ${chosen.name}, each asset gets a new revision, and moved opportunities are marked as set by an admin.`;
  else if (required) hint = 'Opportunities use it, so choose where they go.';
  return (
    <div className="field">
      <label htmlFor="reject-replacement">Move them to…</label>
      <select id="reject-replacement" required={required} autoFocus value={value} aria-describedby="reject-replacement-hint" onChange={(e) => onChange(e.target.value)}>
        <option value="" disabled={required}>
          {required ? 'Choose an approved industry' : 'No replacement: remove the tag'}
        </option>
        {choices.map((i) => (
          <option key={i.id} value={i.id}>{i.name}</option>
        ))}
      </select>
      <span id="reject-replacement-hint" className="hint">
        {hint} Research may still propose "{industry.name}" for a different idea later.
      </span>
    </div>
  );
}

function RejectBody({ industry, counts, choices, value, onChange }: { industry: Industry; counts: RejectPreview | undefined; choices: Industry[]; value: string; onChange: (id: string) => void }) {
  if (!counts) return <output className="hint">Counting what uses it…</output>;
  if (!usage(counts)) return <p>Nothing uses it. Research may still propose "{industry.name}" for a different idea later.</p>;
  return (
    <>
      <p>Uses it now: {usage(counts)}.</p>
      {choices.length === 0 ? (
        <div className="alert alert-warning" role="alert">
          <div className="a-body">There is no approved, active industry to move them to. Add or reactivate one first.</div>
        </div>
      ) : (
        <ReplacementField industry={industry} counts={counts} choices={choices} value={value} onChange={onChange} />
      )}
    </>
  );
}

/** Reject a proposed industry: whatever uses it moves to the replacement the reviewer picks, which is required while
 *  opportunities use it; without one, assets and success cases only lose the tag. */
export function RejectIndustryDialog({ industry, choices, onClose }: { industry: Industry; choices: Industry[]; onClose: () => void }) {
  const reject = useRejectIndustry();
  const preview = useRejectPreview(industry.id);
  const [replacementId, setReplacementId] = useState('');
  const counts = preview.data;
  const required = (counts?.opportunities ?? industry.idea_count) > 0;

  return (
    <Modal
      title={`Reject "${industry.name}"?`}
      subtitle="Whatever uses it moves to the industry you choose, and the proposal is removed."
      size="narrow"
      onClose={onClose}
      onSubmit={() => reject.mutate({ id: industry.id, replacement_industry_id: replacementId || null }, { onSuccess: onClose })}
      footer={
        <>
          <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <ActionButton
            state={buttonState(reject)}
            className="btn btn-danger"
            pendingLabel="Rejecting…"
            doneLabel="Rejected"
            label={replacementId ? 'Reject and move' : 'Reject industry'}
            disabled={!counts || (required && !replacementId)}
          />
        </>
      }
    >
      <div className="stack-md">
        <FormError error={reject.error ?? preview.error} />
        <RejectBody industry={industry} counts={counts} choices={choices} value={replacementId} onChange={setReplacementId} />
      </div>
    </Modal>
  );
}
